import { darken, lighten, rgba } from 'polished';
import { ENABLE_QUARTZ_COLORS } from '../../constants/api';
import { COLOR_TOKENS } from '../../constants/quartz/color_tokens';
import { getMigratedColors, isQuartzColorsEnabled } from '../../constants/quartz/migrated_colors';

export { isQuartzColorsEnabled };

const createRootColors = migratedColors => ({
    AZURE: migratedColors.ravenAzure,
    BLACK: migratedColors.ravenBlack,
    BLACK_2: migratedColors.ravenBlack2,
    BLACK_3: migratedColors.ravenBlack3,
    BLUE: migratedColors.blue,
    COMPONENTS_TABLE_TITLE_ROW: migratedColors.componentsTableTitleRow,
    DARK: migratedColors.ravenDark,
    DARKER: migratedColors.ravenDarker,
    DESC_GREY: migratedColors.grey23,
    ERROR: migratedColors.ravenError,
    FOCUS: migratedColors.ravenFocus,
    GOLD: migratedColors.ravenGold,
    GREY: migratedColors.ravenGrey,
    GREY_2: migratedColors.dark,
    GREY_3: migratedColors.ravenGrey3,
    GREY_4: migratedColors.ravenGrey4,
    GREY_5: migratedColors.ravenGrey5,
    GREY_6: migratedColors.ravenGrey6,
    GREY_7: migratedColors.grey5,
    GREY_8: migratedColors.ravenGrey8,
    GREY_9: migratedColors.lightest,
    GREY_10: migratedColors.tableGrey,
    INPUT: migratedColors.ravenInput,
    INPUT_DISABLED: migratedColors.ravenInputDisabled,
    LIGHT: migratedColors.ravenLight,
    LIGHTER: migratedColors.grey15,
    LIGHT_2: migratedColors.ravenLight2,
    MUTED: migratedColors.ravenMuted,
    ORCHID: migratedColors.ravenOrchid,
    PLACEHOLDER: migratedColors.ravenPlaceholder,
    PRE_ADDED: migratedColors.ravenPreAdded,
    PRE_DELETED: migratedColors.ravenPreDeleted,
    PRIMARY: migratedColors.ravenPrimary,
    SAPPHIRE: migratedColors.ravenSapphire,
    SECONDARY: migratedColors.ravenSecondary,
    SOFT_BLUE: migratedColors.ravenSoftBlue,
    SUCCESS: migratedColors.ravenSuccess,
    TEXT: migratedColors.grey19,
    TOOLBAR: migratedColors.ravenToolbar,
    WARNING: migratedColors.ravenWarning,
    WHITE: migratedColors.white,
});

const createColors = (migratedColors, quartz = false) => {
    const rootColors = createRootColors(migratedColors);
    const colors = {
        ...rootColors,
        ACTIVE: rgba(rootColors.BLACK, 0.54),
        DISABLED: rgba(rootColors.BLACK, 0.26),
        DISABLED_BG: rgba(rootColors.BLACK, 0.12),
        DIVIDER: rootColors.MUTED,
        HOVER: rgba(rootColors.BLACK, 0.08),
        SELECTED: rgba(rootColors.BLACK, 0.14),
        TEXT_DARK: darken(0.3, rootColors.MUTED),
        PRIMARY_GRADIENT: `linear-gradient(to top, ${rootColors.PRIMARY}, ${migratedColors.literal0C60A1})`,
        SECONDARY_GRADIENT: `linear-gradient(to top, ${migratedColors.literal1991EB}, ${rootColors.SECONDARY})`,
        WARNING_GRADIENT: `linear-gradient(to bottom, ${migratedColors.literalF9AB44}, ${migratedColors.literalF9AB44} 1%, ${rootColors.WARNING})`,
        ERROR_GRADIENT: `linear-gradient(to bottom, ${migratedColors.literalCE1C32}, ${migratedColors.literalFF5267} 1%, ${rootColors.ERROR})`,
        SUCCESS_GRADIENT: `linear-gradient(to bottom, ${lighten(0.2, rootColors.SUCCESS)}, ${
            rootColors.SUCCESS
        })`,
        LIGHT_GRADIENT: `linear-gradient(to bottom, ${rootColors.LIGHT}, ${darken(
            0.2,
            rootColors.LIGHT
        )})`,
        PAGINATION_BUTTON_GRADIENT: `linear-gradient(to top, ${migratedColors.literalF2f4f7}, ${migratedColors.literalFfffff})`,
    };

    if (!quartz) return colors;

    return {
        ...colors,
        INTERACTIVE: COLOR_TOKENS.text.selected,
        PRIMARY_SURFACE: COLOR_TOKENS.button.primary.background,
        PRIMARY_GRADIENT: `linear-gradient(to top, ${rootColors.PRIMARY}, ${COLOR_TOKENS.text.linkHover})`,
        WARNING_GRADIENT: `linear-gradient(to bottom, ${lighten(0.2, rootColors.WARNING)}, ${
            rootColors.WARNING
        })`,
        ERROR_GRADIENT: `linear-gradient(to bottom, ${lighten(0.2, rootColors.ERROR)}, ${
            rootColors.ERROR
        })`,
        SUCCESS_GRADIENT: `linear-gradient(to bottom, ${lighten(0.2, rootColors.SUCCESS)}, ${
            rootColors.SUCCESS
        })`,
    };
};

const LEGACY_MIGRATED_COLORS = getMigratedColors(false);
const QUARTZ_MIGRATED_COLORS = getMigratedColors(true);

export const COLORS = createColors(LEGACY_MIGRATED_COLORS);
export const QUARTZ_COLORS = createColors(QUARTZ_MIGRATED_COLORS, true);

const createPalette = (COLORS, migratedColors) => ({
    COLORS,
    action: {
        active: COLORS.ACTIVE,
        disabled: COLORS.DISABLED,
        disabledBackground: COLORS.DISABLED_BG,
        hover: COLORS.HOVER,
        hoverOpacity: 0.08,
        selected: COLORS.SELECTED,
    },
    genesisaction: {
        color: COLORS.GREY_3,
        dark: darken(0.3, COLORS.GREY_3),
        light: lighten(0.2, COLORS.GREY_3),
        text: COLORS.WHITE,
        borderColor: COLORS.GREY_3,
        gradientColor: COLORS.GREY_3,
    },
    zebraColor: {
        color: COLORS.GREY_9,
        dark: darken(0.3, COLORS.GREY_9),
        light: lighten(0.2, COLORS.GREY_9),
        text: COLORS.WHITE,
        borderColor: COLORS.GREY_9,
        gradientColor: COLORS.GREY_9,
    },
    background: {
        color: migratedColors.ravenInputDisabled,
        white: COLORS.WHITE,
    },
    thumbNail: {
        color: COLORS.GREY_4,
    },
    common: {
        black: COLORS.BLACK,
        dark: COLORS.DARK,
        darker: COLORS.DARKER,
        divider: COLORS.DIVIDER,
        input: COLORS.INPUT,
        inputDisabled: COLORS.INPUT_DISABLED,
        light: COLORS.LIGHT,
        lighter: COLORS.LIGHTER,
        placeholder: COLORS.PLACEHOLDER,
        toolbar: COLORS.TOOLBAR,
        white: COLORS.WHITE,
        grey: COLORS.GREY,
    },
    primary: {
        color: COLORS.PRIMARY,
        dark: darken(0.3, COLORS.PRIMARY),
        light: lighten(0.2, COLORS.PRIMARY),
        text: COLORS.WHITE,
        gradientColor: COLORS.PRIMARY_GRADIENT,
        borderColor: COLORS.PRIMARY,
    },
    secondary: {
        color: COLORS.SECONDARY,
        dark: darken(0.3, COLORS.PRIMARY),
        light: lighten(0.2, COLORS.PRIMARY),
        text: COLORS.WHITE,
        gradientColor: COLORS.SECONDARY_GRADIENT,
        borderColor: COLORS.SECONDARY,
    },
    error: {
        color: COLORS.ERROR,
        dark: darken(0.3, COLORS.ERROR),
        light: lighten(0.2, COLORS.ERROR),
        text: COLORS.WHITE,
        gradientColor: COLORS.ERROR_GRADIENT,
        borderColor: COLORS.ERROR,
    },
    warning: {
        color: COLORS.WARNING,
        dark: darken(0.3, COLORS.WARNING),
        light: lighten(0.2, COLORS.WARNING),
        text: COLORS.WHITE,
        gradientColor: COLORS.WARNING_GRADIENT,
        borderColor: COLORS.WARNING,
    },
    success: {
        color: COLORS.SUCCESS,
        dark: darken(0.3, COLORS.SUCCESS),
        light: lighten(0.2, COLORS.SUCCESS),
        text: COLORS.WHITE,
        gradientColor: COLORS.SUCCESS_GRADIENT,
        borderColor: COLORS.SUCCESS,
    },
    dark: {
        color: COLORS.BLACK,
        dark: darken(0.3, COLORS.BLACK),
        light: lighten(0.2, COLORS.BLACK),
        text: COLORS.WHITE,
        gradientColor: COLORS.BLACK,
        borderColor: COLORS.BLACK,
    },
    light: {
        color: COLORS.LIGHT,
        dark: darken(0.3, COLORS.LIGHT),
        light: lighten(0.2, COLORS.LIGHT),
        text: COLORS.WHITE,
        gradientColor: COLORS.LIGHT_GRADIENT,
        borderColor: COLORS.LIGHT,
    },
    white: {
        color: COLORS.WHITE,
        dark: darken(0.3, COLORS.WHITE),
        light: COLORS.WHITE,
        text: COLORS.DARK,
        gradientColor: COLORS.WHITE,
        borderColor: migratedColors.literalDDE1EF,
    },
    link: {
        color: COLORS.PRIMARY,
        dark: darken(0.3, COLORS.PRIMARY),
        light: lighten(0.2, COLORS.PRIMARY),
        text: COLORS.WHITE,
        gradientColor: COLORS.PRIMARY_GRADIENT,
        borderColor: COLORS.PRIMARY,
    },
    muted: {
        color: COLORS.MUTED,
        dark: darken(0.3, COLORS.MUTED),
        light: lighten(0.2, COLORS.MUTED),
        text: COLORS.TEXT,
        gradientColor: COLORS.MUTED,
        borderColor: COLORS.MUTED,
    },
    actions: {
        text: COLORS.DARK,
        gradientColor: migratedColors.tableGrey,
        color: COLORS.PRIMARY_GRADIENT,
        hoverColor: COLORS.WHITE,
        borderColor: COLORS.MUTED,
    },
    checkbox: {
        color: COLORS.PRIMARY,
    },
    paginationButton: {
        text: COLORS.LIGHT,
        gradientColor: COLORS.PAGINATION_BUTTON_GRADIENT,
        color: COLORS.PRIMARY_GRADIENT,
        hoverColor: COLORS.WHITE,
        borderColor: COLORS.MUTED,
    },
    text: {
        color: COLORS.TEXT,
        dark: COLORS.DARK,
        darker: COLORS.DARKER,
        light: COLORS.LIGHT,
        lighter: COLORS.LIGHTER,
    },
    advancedSearch: {
        color: lighten(0.15, COLORS.TEXT),
        dark: darken(0.3, COLORS.MUTED),
        light: lighten(0.2, COLORS.MUTED),
        text: COLORS.TEXT,
        gradientColor: COLORS.MUTED,
        borderColor: COLORS.MUTED,
    },
    notificationViewMoreButton: {
        color: COLORS.TOOLBAR,
        light: lighten(0.2, COLORS.TOOLBAR),
        text: darken(0.7, COLORS.TOOLBAR),
        gradientColor: COLORS.TOOLBAR,
        borderColor: COLORS.TOOLBAR,
        hoverColor: darken(0.4, COLORS.DARK),
    },
    notificationHoverColor: {
        color: darken(0.1, COLORS.TOOLBAR),
    },
    notificationIcon: {
        color: COLORS.WHITE,
    },
    notificationMessage: {
        color: migratedColors.ravenPrimary,
    },
    notificationCounter: {
        color: migratedColors.literalD93232,
    },
    policyNew: {
        color: migratedColors.literalF57F17,
    },
    policyMuted: {
        color: migratedColors.literalF5f5f5,
    },
    policy: {
        color: migratedColors.literalF5f5f5,
        link: migratedColors.literal1577C6,
    },
    handbooks: {
        states: {
            multistate: migratedColors.literalF5a623,
            federal: migratedColors.literal1a91eb,
            onestate: migratedColors.literal7ed321,
        },
        policyType: {
            optional: migratedColors.literal2F6CD0,
            bestpractice: migratedColors.literal404D58,
            required: migratedColors.literalE62C14,
        },
    },
    errorAlert: {
        borderColor: migratedColors.literalF5c6cb,
        backgroundColor: migratedColors.literalF8d7da,
        color: migratedColors.literal721c24,
    },
    tableRowHighlight: {
        color: COLORS.BLUE,
    },
    tableRowStyledLight: {
        color: COLORS.COMPONENTS_TABLE_TITLE_ROW,
    },
});

export const LEGACY_PALETTE = createPalette(COLORS, LEGACY_MIGRATED_COLORS);
const QUARTZ_BASE_PALETTE = createPalette(QUARTZ_COLORS, QUARTZ_MIGRATED_COLORS);

export const QUARTZ_PALETTE = {
    ...QUARTZ_BASE_PALETTE,
    COLORS: QUARTZ_COLORS,
    action: {
        ...QUARTZ_BASE_PALETTE.action,
        active: QUARTZ_COLORS.ACTIVE,
        disabled: QUARTZ_COLORS.DISABLED,
        disabledBackground: QUARTZ_COLORS.DISABLED_BG,
        hover: QUARTZ_COLORS.HOVER,
        selected: QUARTZ_COLORS.SELECTED,
    },
    common: {
        ...QUARTZ_BASE_PALETTE.common,
        black: QUARTZ_COLORS.BLACK,
    },
    primary: {
        ...QUARTZ_BASE_PALETTE.primary,
        backgroundColor: QUARTZ_COLORS.PRIMARY_SURFACE,
        backgroundText: COLOR_TOKENS.button.primary.text,
        borderColor: QUARTZ_COLORS.PRIMARY,
        color: QUARTZ_COLORS.PRIMARY,
        dark: darken(0.3, QUARTZ_COLORS.PRIMARY),
        gradientColor: QUARTZ_COLORS.PRIMARY_GRADIENT,
        light: lighten(0.2, QUARTZ_COLORS.PRIMARY),
    },
    secondary: {
        ...QUARTZ_BASE_PALETTE.secondary,
        dark: darken(0.3, QUARTZ_COLORS.PRIMARY),
        light: lighten(0.2, QUARTZ_COLORS.PRIMARY),
    },
    error: {
        ...QUARTZ_BASE_PALETTE.error,
        backgroundColor: COLOR_TOKENS.feedback.error.background,
        borderColor: QUARTZ_COLORS.ERROR,
        color: QUARTZ_COLORS.ERROR,
        dark: darken(0.3, QUARTZ_COLORS.ERROR),
        gradientColor: QUARTZ_COLORS.ERROR_GRADIENT,
        light: lighten(0.2, QUARTZ_COLORS.ERROR),
    },
    warning: {
        ...QUARTZ_BASE_PALETTE.warning,
        backgroundColor: COLOR_TOKENS.feedback.warning.background,
        borderColor: QUARTZ_COLORS.WARNING,
        color: QUARTZ_COLORS.WARNING,
        dark: darken(0.3, QUARTZ_COLORS.WARNING),
        gradientColor: QUARTZ_COLORS.WARNING_GRADIENT,
        light: lighten(0.2, QUARTZ_COLORS.WARNING),
        text: COLOR_TOKENS.text.primary,
    },
    success: {
        ...QUARTZ_BASE_PALETTE.success,
        backgroundColor: COLOR_TOKENS.feedback.success.background,
        borderColor: QUARTZ_COLORS.SUCCESS,
        color: QUARTZ_COLORS.SUCCESS,
        dark: darken(0.3, QUARTZ_COLORS.SUCCESS),
        gradientColor: QUARTZ_COLORS.SUCCESS_GRADIENT,
        light: lighten(0.2, QUARTZ_COLORS.SUCCESS),
    },
    dark: {
        ...QUARTZ_BASE_PALETTE.dark,
        borderColor: QUARTZ_COLORS.BLACK,
        color: QUARTZ_COLORS.BLACK,
        dark: darken(0.3, QUARTZ_COLORS.BLACK),
        gradientColor: QUARTZ_COLORS.BLACK,
        light: lighten(0.2, QUARTZ_COLORS.BLACK),
    },
    link: {
        ...QUARTZ_BASE_PALETTE.link,
        borderColor: QUARTZ_COLORS.PRIMARY,
        color: QUARTZ_COLORS.PRIMARY,
        dark: darken(0.3, QUARTZ_COLORS.PRIMARY),
        gradientColor: QUARTZ_COLORS.PRIMARY_GRADIENT,
        light: lighten(0.2, QUARTZ_COLORS.PRIMARY),
    },
    actions: {
        ...QUARTZ_BASE_PALETTE.actions,
        color: QUARTZ_COLORS.PRIMARY_GRADIENT,
    },
    checkbox: {
        color: QUARTZ_COLORS.PRIMARY,
    },
    paginationButton: {
        ...QUARTZ_BASE_PALETTE.paginationButton,
        color: QUARTZ_COLORS.PRIMARY_GRADIENT,
    },
    notificationMessage: {
        color: QUARTZ_COLORS.PRIMARY,
    },
    notificationCounter: {
        color: QUARTZ_COLORS.ERROR,
    },
    policyNew: {
        color: QUARTZ_COLORS.WARNING,
    },
    handbooks: {
        ...QUARTZ_BASE_PALETTE.handbooks,
        policyType: {
            ...QUARTZ_BASE_PALETTE.handbooks.policyType,
            required: QUARTZ_COLORS.ERROR,
        },
    },
    errorAlert: {
        backgroundColor: COLOR_TOKENS.feedback.error.background,
        borderColor: COLOR_TOKENS.feedback.error.border,
        color: COLOR_TOKENS.feedback.error.text,
    },
};

export const getPalette = flagValue =>
    isQuartzColorsEnabled(flagValue) ? QUARTZ_PALETTE : LEGACY_PALETTE;

export const PALETTE = getPalette(ENABLE_QUARTZ_COLORS);
